/**
 * api.js — Instance Axios configurée pour le backend Django.
 * - URL de base : /api/private (endpoints authentifiés par session)
 * - Attache automatiquement le jeton CSRF aux requêtes de modification
 * - Réessaie automatiquement une fois en cas d'échec CSRF (jeton expiré)
 */
import axios from 'axios';
import i18n from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const baseHeaders = { 'Content-Type': 'application/json' };

const api = axios.create({
  baseURL: `${BACKEND_URL}/api/private`,
  withCredentials: true,
  headers: baseHeaders,
});

export const publicApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: baseHeaders,
});

/**
 * Lit la valeur d'un cookie par son nom depuis document.cookie.
 * @param {string} name - Nom du cookie à rechercher.
 * @returns {string|null} Valeur décodée du cookie, ou null si introuvable.
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function attachLanguageHeader(config) {
  const language = i18n.resolvedLanguage || i18n.language;
  if (language) config.headers['Accept-Language'] = language;
  return config;
}

// Attache l'en-tête du jeton CSRF Django à chaque requête de modification
api.interceptors.request.use(config => {
  attachLanguageHeader(config);
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    const token = getCookie('csrftoken');
    if (token) config.headers['X-CSRFToken'] = token;
  }
  return config;
});

publicApi.interceptors.request.use(config => attachLanguageHeader(config));

// En cas d'erreur 403 CSRF, rafraîchir le jeton et réessayer la requête une fois
api.interceptors.response.use(
  response => response,
  async error => {
    if (error.response?.status === 403 && error.response?.data?.error === 'CSRF verification failed' && !error.config._csrfRetry) {
      error.config._csrfRetry = true; // Empêcher les boucles de réessai infinies
      await initCsrf();
      const token = getCookie('csrftoken');
      if (token) error.config.headers['X-CSRFToken'] = token;
      return api(error.config);
    }
    return Promise.reject(error);
  }
);

/**
 * Récupère un jeton CSRF frais depuis le backend et le stocke en cookie.
 * Appelée une fois au démarrage de l'application et à nouveau si une requête échoue à la validation CSRF.
 * @returns {Promise<void>}
 */
export async function initCsrf() {
  await api.get('/auth/csrf/');
}

export default api;
